import { z } from 'zod';
import { useFrontendTool } from '@/hooks/useFrontendTool';
import { useTodoList } from './TodoListContext';

const todoStatusSchema = z.enum(['pending', 'in-progress', 'done', 'skipped']);

const todoItemSchema = z.object({
  id: z.string().describe('Stable identifier for this task'),
  label: z.string().describe('Short human-readable description'),
  status: todoStatusSchema,
  detail: z
    .string()
    .optional()
    .describe('Internal notes or metadata for tracking progress'),
});

const todoItemPatchSchema = z.object({
  id: z.string().describe('ID of the item to update'),
  label: z.string().optional().describe('New label (omit to keep current)'),
  status: todoStatusSchema
    .optional()
    .describe('New status (omit to keep current)'),
  detail: z
    .string()
    .optional()
    .describe('New detail text (omit to keep current)'),
});

export function useTodoListTools() {
  const { setTodoList, updateTodoItems, clearTodoList } = useTodoList();

  const setTool = useFrontendTool({
    name: 'set_todo_list',
    description:
      'Create or fully replace the task checklist shown to the user. Call immediately when you identify a multi-part request or multi-step task. Also call to revise the plan when items are added, removed, or reordered.',
    parameters: z.object({
      title: z
        .string()
        .optional()
        .describe('Optional heading for the task list'),
      items: z
        .array(todoItemSchema)
        .describe('Complete item list (replaces any existing list)'),
    }),
    execute: (args) => {
      if (args.items.length === 0) {
        clearTodoList();
      } else {
        setTodoList({ title: args.title, items: args.items });
      }
    },
  });

  const updateTool = useFrontendTool({
    name: 'update_todo_items',
    description:
      'Patch individual items in the task checklist by ID. Call BEFORE writing response text to mark the current item in-progress, and again to mark it done when finished. The user sees updates in real time — never let statuses go stale.',
    parameters: z.object({
      items: z
        .array(todoItemPatchSchema)
        .min(1)
        .describe('Items to patch by ID'),
    }),
    execute: (args) => {
      updateTodoItems(args.items);
    },
  });

  const clearTool = useFrontendTool({
    name: 'clear_todo_list',
    description:
      'Remove the task checklist entirely. Only use after all items are done and the user has seen the completed list.',
    parameters: z.object({}),
    execute: () => {
      clearTodoList();
    },
  });

  return [setTool, updateTool, clearTool] as const;
}
